import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useConnect, useDisconnect, useSignMessage } from "wagmi";
import { injectedConnector } from "../walletConfig";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const parseSummary = (summary) => {
  if (!summary) return null;
  if (typeof summary === "string") {
    try {
      return JSON.parse(summary);
    } catch {
      return null;
    }
  }
  return summary;
};

const parseRecipients = (recipients) => {
  if (!recipients) return [];
  if (typeof recipients === "string") {
    try {
      return JSON.parse(recipients);
    } catch {
      return [];
    }
  }
  return Array.isArray(recipients) ? recipients : [];
};

const readErrorMessage = async (response, fallback) => {
  try {
    const text = await response.text();
    if (!text) return fallback;
    try {
      const data = JSON.parse(text);
      return data?.error || data?.message || text;
    } catch {
      return text;
    }
  } catch {
    return fallback;
  }
};

const AdminListingsPage = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const apiKey = process.env.REACT_APP_API_KEY;

  const { address, isConnected } = useAccount();
  const { connect, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();

  const [requests, setRequests] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [denyReasons, setDenyReasons] = useState({});
  const [checksCache, setChecksCache] = useState({});

  const adminAddress = useMemo(() => {
    return (
      process.env.REACT_APP_ADMIN_SIGNER_ADDRESS ||
      process.env.REACT_APP_MINT_CONTROLLER_ADDRESS ||
      ""
    ).toLowerCase();
  }, []);

  const isAdmin = useMemo(() => {
    if (!address) return false;
    return address.toLowerCase() === adminAddress;
  }, [address, adminAddress]);

  const showStatus = useCallback((message, tone = "info") => {
    setStatusMessage(message);
    setStatusTone(tone);
  }, []);

  const adminFetch = useCallback(
    async (path, options = {}) => {
      if (!backendUrl || !apiKey) {
        throw new Error("Missing backend configuration (REACT_APP_BACKEND_URL / REACT_APP_API_KEY).");
      }
      if (!address) {
        throw new Error("Connect admin wallet first");
      }
      const nonceRes = await fetch(`${backendUrl}/listing/admin/nonce?address=${address}`, {
        headers: { "x-api-key": apiKey },
      });
      if (!nonceRes.ok) {
        const errorText = await readErrorMessage(nonceRes, "Failed to get admin nonce");
        throw new Error(errorText);
      }
      const { message } = await nonceRes.json();
      const signature = await signMessageAsync({ message });
      const headers = {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "x-admin-address": address,
        "x-admin-signature": signature,
        "x-admin-message": message,
        ...(options.headers || {}),
      };
      return fetch(`${backendUrl}${path}`, { ...options, headers });
    },
    [backendUrl, apiKey, address, signMessageAsync]
  );

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminFetch("/listing/requests");
      if (!response.ok) {
        const errorText = await readErrorMessage(response, "Failed to load requests");
        throw new Error(errorText);
      }
      const result = await response.json();
      setRequests(result.requests || []);
      showStatus("Listing requests loaded.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Unable to load requests.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, showStatus]);

  useEffect(() => {
    if (isConnected && isAdmin) {
      loadRequests();
    } else {
      setRequests([]);
    }
  }, [isConnected, isAdmin, loadRequests]);

  useEffect(() => {
    if (connectError) {
      showStatus(connectError.message || "Unable to connect wallet.", "error");
    }
  }, [connectError, showStatus]);

  const handleRunChecks = async (id) => {
    setIsLoading(true);
    try {
      const response = await adminFetch(`/listing/requests/${id}/checks`, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to run checks");
      }
      const result = await response.json();
      setChecksCache((prev) => ({ ...prev, [id]: result.checks }));
      setRequests((prev) =>
        prev.map((item) => (item.id === id ? { ...item, check_status: result.overall, check_summary: result.checks } : item))
      );
      showStatus("Checks completed.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Unable to run checks.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setIsLoading(true);
    try {
      const response = await adminFetch(`/listing/requests/${id}/approve`, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to approve listing");
      }
      const result = await response.json();
      setRequests((prev) => prev.map((item) => (item.id === id ? result.listing : item)));
      showStatus("Listing approved.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Unable to approve listing.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeploy = async (id) => {
    setIsLoading(true);
    try {
      const response = await adminFetch(`/listing/requests/${id}/deploy`, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to deploy wrapped asset");
      }
      const result = await response.json();
      setRequests((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                besc_wrapped_address: result.asset?.besc_wrapped_address || item.besc_wrapped_address,
                deploy_tx_hash: result.asset?.deploy_tx_hash || item.deploy_tx_hash,
                verified_at: result.asset?.verified_at || item.verified_at,
                asset_status: result.asset?.status || item.asset_status,
              }
            : item
        )
      );
      showStatus("Wrapped asset deployed.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Unable to deploy wrapped asset.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (id) => {
    setIsLoading(true);
    try {
      const response = await adminFetch(`/listing/requests/${id}/verify`, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to verify wrapped asset");
      }
      const result = await response.json();
      setRequests((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                besc_wrapped_address: result.asset?.besc_wrapped_address || item.besc_wrapped_address,
                deploy_tx_hash: result.asset?.deploy_tx_hash || item.deploy_tx_hash,
                verified_at: result.asset?.verified_at || item.verified_at,
                asset_status: result.asset?.status || item.asset_status,
              }
            : item
        )
      );
      showStatus("Wrapped asset verified.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Unable to verify wrapped asset.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeny = async (id) => {
    const reason = denyReasons[id] || "";
    setIsLoading(true);
    try {
      const response = await adminFetch(`/listing/requests/${id}/deny`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to deny listing");
      }
      const result = await response.json();
      setRequests((prev) => prev.map((item) => (item.id === id ? result.listing : item)));
      showStatus("Listing denied.", "success");
    } catch (error) {
      console.error(error);
      showStatus(error.message || "Unable to deny listing.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
    if (filter === "all") return requests;
    return requests.filter((req) => req.status === filter);
  }, [requests, filter]);

  return (
    <section className="admin-page">
      <div className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Bridge ops</p>
          <h1>Listing Approvals</h1>
          <p>Approve or deny new assets. Run checks to detect transfer taxes, limits, and trading locks.</p>
        </div>
        <Link to="/" className="secondary-button ghost admin-page__back">
          Back to bridge
        </Link>
      </div>

      <div className="admin-page__panel">
        <div className="admin-page__wallet">
          {isConnected ? (
            <div className="admin-page__wallet-info">
              <span>Connected admin wallet</span>
              <strong>{address}</strong>
              <button className="secondary-button mini" onClick={() => disconnect()}>
                Disconnect
              </button>
            </div>
          ) : (
            <div className="admin-page__wallet-info">
              <span>Connect the governance wallet to manage listings.</span>
              <button className="primary-button" onClick={() => connect({ connector: injectedConnector })}>
                Connect admin wallet
              </button>
            </div>
          )}
        </div>

        <div className="admin-page__controls">
          <div className="admin-page__filters">
            {["all", "pending", "approved", "denied"].map((status) => (
              <button
                key={status}
                className={`secondary-button mini ${filter === status ? "is-active" : ""}`}
                onClick={() => setFilter(status)}
                type="button"
                disabled={!isAdmin}
              >
                {status}
              </button>
            ))}
          </div>
          <button className="secondary-button" onClick={loadRequests} disabled={!isAdmin || isLoading}>
            Refresh
          </button>
        </div>

        {isConnected && !isAdmin && (
          <div className="status-banner status-banner--error">
            <span className="status-banner__message">This wallet is not authorized for admin access.</span>
          </div>
        )}

        {statusMessage && (
          <div className={`status-banner status-banner--${statusTone}`}>
            <span>{statusMessage}</span>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="admin-page__list">
        {filteredRequests.length === 0 && (
          <div className="admin-page__empty">
            <p>No listing requests found.</p>
          </div>
        )}
        {filteredRequests.map((req) => {
          const summary = parseSummary(req.check_summary) || checksCache[req.id];
          const recipients = parseRecipients(req.tax_recipients);
          return (
            <div key={req.id} className="admin-card">
              <div className="admin-card__header">
                <div>
                  <h2>
                    {req.token_name || "Token"} ({req.symbol || "—"})
                  </h2>
                  <p>
                    {req.chain?.toUpperCase()} • {req.token_address}
                  </p>
                </div>
                <div className="admin-card__badges">
                  <span className={`admin-badge admin-badge--${req.status || "pending"}`}>{req.status || "pending"}</span>
                  <span className={`admin-badge admin-badge--${req.check_status || "pending"}`}>
                    checks: {req.check_status || "pending"}
                  </span>
                  {req.asset_status && (
                    <span className={`admin-badge admin-badge--${req.asset_status}`}>
                      asset: {req.asset_status}
                    </span>
                  )}
                </div>
              </div>

              <div className="admin-card__body">
                <div>
                  <p>
                    <strong>Contact</strong> {req.contact_email || "—"}
                  </p>
                  <p>
                    <strong>Owner</strong> {req.owner_address || "—"}
                  </p>
                  <p>
                    <strong>Submitted</strong> {formatDate(req.created_at)}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Wrapped</strong> {req.besc_wrapped_address || "—"}
                  </p>
                  <p>
                    <strong>Deploy tx</strong> {req.deploy_tx_hash || "—"}
                  </p>
                  <p>
                    <strong>Test tx</strong> {req.test_tx_hash || "—"}
                  </p>
                </div>
              </div>

              {req.has_tax && (
                <div className="admin-card__tax">
                  <p>
                    <strong>Tax rate</strong> {(Number(req.tax_bps || 0) / 100).toFixed(2)}%
                  </p>
                  {recipients.length > 0 && (
                    <div className="admin-card__tax-list">
                      {recipients.map((entry, idx) => (
                        <div key={`${entry.label || entry.wallet}-${idx}`}>
                          <span>{entry.label || "wallet"}</span>
                          <strong>{(Number(entry.bps || 0) / 100).toFixed(2)}%</strong>
                          <span>{entry.wallet}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {summary && (
                <div className="admin-card__checks">
                  <div>
                    <strong>Metadata</strong>
                    <span className={`admin-pill admin-pill--${summary.metadata?.status || "pending"}`}>
                      {summary.metadata?.status || "pending"}
                    </span>
                    <p>{summary.metadata?.message || "Metadata check pending."}</p>
                  </div>
                  <div>
                    <strong>Decimals</strong>
                    <span className={`admin-pill admin-pill--${summary.decimalsMatch?.status || "pending"}`}>
                      {summary.decimalsMatch?.status || "pending"}
                    </span>
                    <p>{summary.decimalsMatch?.message || "Decimals check pending."}</p>
                  </div>
                  <div>
                    <strong>Transfer tax</strong>
                    <span className={`admin-pill admin-pill--${summary.transferTax?.status || "pending"}`}>
                      {summary.transferTax?.status || "pending"}
                    </span>
                    <p>{summary.transferTax?.message || "Transfer tax check pending."}</p>
                  </div>
                  <div>
                    <strong>Limits</strong>
                    <span className={`admin-pill admin-pill--${summary.limits?.status || "pending"}`}>
                      {summary.limits?.status || "pending"}
                    </span>
                    <p>{summary.limits?.message || "Limits check pending."}</p>
                  </div>
                </div>
              )}

              <div className="admin-card__actions">
                <button className="secondary-button" onClick={() => handleRunChecks(req.id)} disabled={isLoading}>
                  Run checks
                </button>
                <button className="primary-button" onClick={() => handleApprove(req.id)} disabled={isLoading}>
                  Approve
                </button>
                {req.status === "approved" && !req.besc_wrapped_address && (
                  <button className="secondary-button" onClick={() => handleDeploy(req.id)} disabled={isLoading}>
                    Deploy
                  </button>
                )}
                {req.besc_wrapped_address && !req.verified_at && (
                  <button className="secondary-button" onClick={() => handleVerify(req.id)} disabled={isLoading}>
                    Verify
                  </button>
                )}
                <div className="admin-card__deny">
                  <input
                    type="text"
                    placeholder="Denial reason (optional)"
                    value={denyReasons[req.id] || ""}
                    onChange={(event) =>
                      setDenyReasons((prev) => ({ ...prev, [req.id]: event.target.value }))
                    }
                  />
                  <button className="secondary-button ghost" onClick={() => handleDeny(req.id)} disabled={isLoading}>
                    Deny
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </section>
  );
};

export default AdminListingsPage;
